import { FC, useMemo } from 'react';
import urlJoin from 'url-join';
import QRCode from 'react-qr-code';

import { Button, ButtonProps } from '@/Components/Button';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { ExclamationSvg } from '@/Components/Icons/ExclamationSvg';
import { LogoSvg } from '@/Components/Icons/LogoSvg';
import { ModalView } from '@/Components/ModalView';
import { AppModalProps, PREVIEW_MODAL_ID } from '@/types/modal.type';
import { FruitLemonSvg } from '@/Components/Icons/FruitLemonSvg';
import { getFlowPreviewMode } from '@/util/getFlowPreviewMode.util';
import {
    NEVER_INSTALLED_VIEW_MODE,
    INSTALL_ISSUE_VIEW_MODE,
    VERIFIED_VIEW_MODE,
    CURRENT_FLOW_VERSION,
    FlowPreviewModalViewMode,
    FlowPreviewVersion,
} from '@/types/flow-preview-mode.type';
import { StrawberrySvg } from '@/Components/Icons/StrawberrySvg';

type FlowPreviewModalProps = AppModalProps<{
    flowPage: string;
    flowCode: string;
    flowName: string;
    workspaceUrl: string;
    workspaceCode: string;
    ignoreButtonLabel?: string;
    isCodeInstalled: boolean;
    installationVerifiedAt: string | null;
    simulatedPreviewEnabled?: boolean;
    // NOTE: Which flow version the preview should show: "current" (currently published)
    //   or "working_copy" (latest Flow Builder changes, not yet published)
    flowVersion?: FlowPreviewVersion;
    // NOTE: Custom view mode, if not provided will be calculated based on the installationVerifiedAt and isCodeInstalled
    viewMode?: FlowPreviewModalViewMode;
    onIgnoreClick?: () => void;
}>;

export const FlowPreviewModal: FC<FlowPreviewModalProps> = ({
    payload,
    onClose,
    onOpen,
}) => {
    const customViewMode = payload?.viewMode;
    const flowName = payload?.flowName;
    const flowPage = payload?.flowPage;
    const flowCode = payload?.flowCode;
    const workspaceUrl = payload?.workspaceUrl;
    const isCodeInstalled = payload?.isCodeInstalled ?? false;
    const installationVerifiedAt = payload?.installationVerifiedAt;
    const workspaceCode = payload?.workspaceCode;
    const ignoreButtonLabel =
        payload?.ignoreButtonLabel ?? 'Ignore and try Preview anyway...';
    const onIgnoreClick = payload?.onIgnoreClick;
    const simulatedPreviewEnabled = payload?.simulatedPreviewEnabled ?? false;
    const flowVersion = payload?.flowVersion ?? CURRENT_FLOW_VERSION;
    const flowVersionParam = `&reelflow_flow_version=${flowVersion}`;
    const simulatedPreviewUrl = `/simulated-preview/${workspaceCode}?reelflow_preview=1&reelflow_activation_url=${encodeURIComponent(flowPage ?? '')}&reelflow_flow=${flowCode ?? ''}${flowVersionParam}`;

    const previewUrl = urlJoin(workspaceUrl || '', flowPage).toString();

    const previewUrlWithParams = useMemo(() => {
        const separator = previewUrl.includes('?') ? '&' : '?';

        return `${previewUrl}${separator}reelflow_preview=1&reelflow_flow=${flowCode}${flowVersionParam}`;
    }, [flowCode, previewUrl, flowVersionParam]);

    const viewMode =
        customViewMode ||
        getFlowPreviewMode(installationVerifiedAt, isCodeInstalled);

    const settingsPageUrl = route('settings.installation', {
        redirect_to: window?.location?.href,
        redirect_label: encodeURIComponent(flowName),
    });

    const ignoreButtonProps: Partial<ButtonProps> = onIgnoreClick
        ? {
              onClick: onIgnoreClick,
              className:
                  'h-9 flex items-center justify-center w-full text-center',
          }
        : {
              onClick: () => {
                  onOpen({
                      id: PREVIEW_MODAL_ID,
                      payload: {
                          ...payload,
                          viewMode: VERIFIED_VIEW_MODE,
                      },
                  });
              },
              className:
                  'h-9 flex items-center justify-center w-full text-center',
          };

    return (
        <ModalView
            maxWidth="md"
            onClose={onClose}
            contentContainerClassName="py-4 text-brand-ocean">
            <div className="flex flex-row items-center justify-end">
                <button
                    title="Close"
                    aria-label="Close"
                    className="mx-4 p-2 transition-all rounded-sm hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            {viewMode === NEVER_INSTALLED_VIEW_MODE && (
                <>
                    <div className="flex flex-row items-center justify-center mb-2">
                        <LogoSvg className="mb-8 -mr-1.5" />
                        <ExclamationSvg />
                    </div>
                    <div className="mx-6 text-center">
                        <p className="text-heading-four font-area-normal font-bold text-brand-ocean">
                            Your Install Code isn't set up yet
                        </p>
                        <p className="mt-4 text-body-text-15 text-brand-ocean">
                            ReelFlow needs a small snippet of code on your
                            website to show Flows to visitors, and to let you
                            preview them.
                        </p>
                    </div>
                    <div className="flex flex-col items-center justify-center mt-4 mb-2 mx-8 gap-y-3">
                        <Button
                            elementType="link"
                            href={settingsPageUrl}
                            variant="primary"
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full mx-8 text-center">
                            Get Install Code
                        </Button>
                        {simulatedPreviewEnabled && (
                            <Button
                                elementType="a"
                                href={simulatedPreviewUrl}
                                target="_blank"
                                variant="default"
                                onClick={onClose}
                                className="h-9 flex items-center justify-center w-full text-center">
                                Not possible? Try a simulated preview
                            </Button>
                        )}
                    </div>
                </>
            )}
            {viewMode === INSTALL_ISSUE_VIEW_MODE && (
                <>
                    <div className="flex flex-row items-center justify-center mb-2">
                        <LogoSvg className="mb-6 -mr-2.5" />
                        <FruitLemonSvg />
                    </div>
                    <div className="mx-6 text-center">
                        <p className="text-heading-four font-area-normal font-bold text-brand-ocean">
                            Your preview might not work
                        </p>
                        <p className="mt-4 text-body-text-15 text-brand-ocean">
                            We can't detect your Install Code on your website,
                            it may have been removed or stopped working.
                        </p>
                    </div>
                    <div className="flex flex-col items-center justify-center mt-4 mb-2 mx-8 gap-y-3">
                        <Button
                            elementType="link"
                            href={settingsPageUrl}
                            variant="primary"
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full text-center">
                            Verify Install Code
                        </Button>
                        <Button variant="default" {...ignoreButtonProps}>
                            {ignoreButtonLabel}
                        </Button>
                    </div>
                </>
            )}
            {viewMode === VERIFIED_VIEW_MODE && (
                <>
                    <div className="flex flex-row items-center justify-center mb-2">
                        <LogoSvg className="mb-8" />
                        <StrawberrySvg />
                    </div>
                    <p className="text-heading-four font-area-normal font-bold text-brand-ocean text-center mx-4">
                        Preview Flow on your website
                    </p>
                    <div className="flex flex-row items-center justify-center mx-8 mt-4">
                        <p className="text-center text-body-text-15 break-words text-blue-500 !w-full">
                            {previewUrl}
                        </p>
                    </div>
                    <div className="mt-6 flex flex-row items-start justify-center text-body-text text-brand-ocean px-6">
                        <div className="bg-white p-1 rounded mr-4">
                            <QRCode
                                value={previewUrlWithParams}
                                size={82}
                                viewBox="0 0 128 128"
                            />
                        </div>
                        <ul className="text-body-text-15 text-left list-disc list-outside ml-2 pl-4 space-y-2">
                            <li>Your visitors won't see it</li>
                            <li>It won't affect your performance metrics</li>
                            <li>Scan QR to preview on mobile</li>
                        </ul>
                    </div>
                    <div className="flex flex-col items-center justify-center mt-6 mb-2 mx-8 gap-y-3">
                        <Button
                            elementType="a"
                            target="_blank"
                            href={previewUrlWithParams}
                            variant="primary"
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full text-center">
                            Open preview
                        </Button>
                        {simulatedPreviewEnabled && (
                            <Button
                                elementType="a"
                                href={simulatedPreviewUrl}
                                target="_blank"
                                variant="default"
                                onClick={onClose}
                                className="h-9 flex items-center justify-center w-full text-center">
                                Try a simulated preview
                            </Button>
                        )}
                    </div>
                </>
            )}
        </ModalView>
    );
};
